import merge from 'deepmerge';
import _unset from 'unset-value';
import isObject from 'isobject';
import createAction from '../createAction.js';
import createDeepEqualSelector from '../createDeepEqualSelector.js';
import DuckSelector from '../DuckSelector.js';
import primitiveDuckCreator from './primitiveDuckCreator.js';
import hasStore from './checks/hasPayloadTypeCheck.js';

const legacyReducer = (state, action, duck) => {
    switch (action.type) {
        case duck.types.ASSIGN: {
            return {
                ...state,
                ...action.payload,
            };
        }

        case duck.types.CLEAR: {
            return {};
        }

        case duck.types.DELETE: {
            const newState = { ...state };
            [].concat(action.payload).forEach((key) => {
                if (key.includes('.')) {
                    _unset(state, key);
                } else {
                    delete newState[key];
                }
            });
            return newState;
        }

        case duck.types.MERGE: {
            hasStore(duck, action, 'object');

            const { value = {}, options = {} } = action.payload;
            return merge(state, value, options);
        }

        case duck.types.RESET: {
            return duck.initialState;
        }

        case duck.types.SET_OBJECT: {
            hasStore(duck, action, 'object');

            return action.payload;
        }

        default:
            return state;
    }
};

const rtkReducer = (builder, duck) => {
    builder
        .addCase(duck.types.ASSIGN, (state, action) => {
            Object.assign(state, action.payload);
        })

        .addCase(duck.types.DELETE, (state, action) => {
            [].concat(action.payload).forEach((key) => {
                if (key.includes('.')) {
                    _unset(state, key);
                } else {
                    delete state[key];
                }
            });
        })

        .addCase(duck.types.MERGE, (state, action) => {
            hasStore(duck, action, 'object');
            const { value = {}, options = {} } = action.payload;
            return merge(state, value, options);
        })

        .addCase(duck.types.SET_OBJECT, (state, action) => action.payload);
};

const objectDuckCreator = (options) =>
    primitiveDuckCreator({
        primitiveState: {},
        ...options,
    }).extend({
        creators: (duck) => ({
            assign: createAction(duck.types.ASSIGN),
            delete: createAction(duck.types.DELETE),
            merge: (...args) => {
                const [value = {}, mergeOptions = {}] = args;
                return {
                    type: duck.types.MERGE,
                    payload: {
                        value,
                        options: mergeOptions,
                    },
                };
            },
            // deprecated - use assign, merge, or setObject
            setKey: (...args) => {
                const payload = {};

                // handle setKey(key, value)
                if (args.length === 2) {
                    const [key, value] = args;
                    payload[key] = value;
                }

                // handle object payloads
                if (isObject(args[0])) {
                    const objectPayload = args[0];

                    if (
                        Object.prototype.hasOwnProperty.call(
                            objectPayload,
                            'key'
                        ) &&
                        Object.prototype.hasOwnProperty.call(
                            objectPayload,
                            'value'
                        )
                    ) {
                        // handle setKey({ key, value })
                        payload[args[0].key] = args[0].value;
                    } else {
                        // handle setKey({ key: value })
                        Object.assign(payload, args[0]);
                    }
                }

                return {
                    type: duck.types.ASSIGN,
                    payload,
                };
            },
            // deprecated - use assign, merge, or setObject
            setKeys: createAction(duck.types.ASSIGN),
            setObject: createAction(duck.types.SET_OBJECT),
        }),
        reducer: options.useRTKReducer ? rtkReducer : legacyReducer,
        selectors: (duck) => ({
            selectKeys: new DuckSelector((_selectors) =>
                createDeepEqualSelector(_selectors.getDuckState, (localState) =>
                    Object.keys(localState)
                )
            ),
            selectValues: new DuckSelector((_selectors) =>
                createDeepEqualSelector(_selectors.getDuckState, (localState) =>
                    Object.values(localState)
                )
            ),
            selectIsEmpty: new DuckSelector((_selectors) =>
                createDeepEqualSelector(
                    _selectors.selectKeys,
                    (keys) => keys.length === 0
                )
            ),
        }),
        types: ['ASSIGN', 'DELETE', 'MERGE', 'SET_OBJECT'],
    });

export { objectDuckCreator as default };
